// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import cn from 'classnames';
import { FormEvent, useEffect, useState } from 'react';
import TextareaAutosize from 'react-autosize-textarea/lib';
import { X } from 'react-feather';
import { api } from '../../helpers/api-helpers';
import Button from '../Button';
import styles from './NPSSurvey.module.css';

export type NPSEvent =
  | 'Study Plan Unlocked'
  | 'Practice Test Complete'
  | 'Subscription Canceled';

export interface NPSSubmission {
  /** The event that triggered this NPS survey prompt */
  event: NPSEvent;
  /** Score selected by the user */
  nps: number | null;
  /** Optional comment entered by the user to explain their score */
  comment?: string;
  /** ID of the current course the user is enrolled in */
  course?: string;
  /** ID of the current product the user is subscribed to */
  product?: string;
}

export interface NPSSurveyProps {
  /** The event that triggered this NPS survey prompt */
  event: NPSEvent;
  /**
   * ID (or IDs) of the current course(s) the user is enrolled in
   *
   * This will only be an array of course IDs if the user cancels an entire product
   */
  course?: string;
  /** ID of the current product the user is subscribed to */
  product?: string;
  /** Text to display directly before the standard text */
  introMessage?: string;
  /** Function to run when the user has completed or canceled the survey */
  onComplete?: () => void;
}

const MAX_COMMENT_LENGTH = 500;

/**
 * Creates an element with the NPS survey prompt
 * @param props Object containing the necessary parameters to submit an NPS survey result
 * @returns Element with the NPS survey prompt
 */
function NPSSurvey(props: NPSSurveyProps): JSX.Element {
  const [score, setScore] = useState<number>();
  const [comment, setComment] = useState<string>();
  const [clickedCancel, setClickedCancel] = useState<boolean>(false);

  const { course, product, event, introMessage, onComplete } = props;

  useEffect(() => setClickedCancel(false), [score, comment]);

  /**
   * Creates an element displaying a selectable rating button
   * @param rating Button's individual score number
   * @returns Element displaying a selectable rating button
   */
  function RatingButton(rating: number): JSX.Element {
    const isActive = score === rating;
    return (
      <button
        key={`rating_${rating}`}
        className={cn(styles.ratingButton, isActive && styles.active)}
        onClick={() => setScore(rating)}
      >
        {rating}
      </button>
    );
  }

  /**
   * Creates an array of selectable rating buttons
   * @returns Array of selectable rating buttons
   */
  function buildRatingButtons(): JSX.Element[] {
    const ratings = Array.from({ length: 10 }, (_, i) => i + 1);
    return ratings.map((rating) => RatingButton(rating));
  }

  /** Handles the user clicking either 'Skip' or 'X' */
  function handleCancel(): void {
    const submission: NPSSubmission = buildSubmission(null);

    if (clickedCancel) {
      api.users
        .submitNPSSurvey({ params: submission })
        .finally(() => onComplete && onComplete());
    } else {
      setClickedCancel(true);
    }
  }

  /** Handles the user clicking 'Submit' */
  function handleSubmit(): void {
    const submission: NPSSubmission = buildSubmission(score || null);

    api.users
      .submitNPSSurvey({ params: submission })
      .finally(() => onComplete && onComplete());
  }

  /**
   * Builds an NPS score submission to be stored in the database
   * @param nps NPS score (`null` if the user canceled the modal without giving a score)
   * @param course ID of the course
   * @returns NPS score submission object to be stored in the database
   */
  function buildSubmission(nps: number | null): NPSSubmission {
    return {
      course,
      product,
      nps,
      comment,
      event,
    };
  }

  /** Handles the user typing into the comment box */
  function handleCommentChange(e: FormEvent<HTMLTextAreaElement>): void {
    setComment(e.currentTarget.value.substring(0, MAX_COMMENT_LENGTH));
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.x}>
        <X onClick={handleCancel} />
      </div>
      <div className={styles.content}>
        <div className={styles.intro}>
          <p>
            <span hidden={!introMessage}>{introMessage} </span>
            Please help us improve 240 Tutoring by answering a quick question
            about your experience.
          </p>
        </div>
        <div className={styles.survey}>
          <p>
            How likely are you to recommend 240 Tutoring to a friend or
            colleague?
          </p>
          <div className={styles.ratingButtonsWrapper}>
            {buildRatingButtons()}
          </div>
          <div className={styles.ratingButtonsDescription}>
            <span>Not likely</span>
            <span>Very likely</span>
          </div>
          <div className={styles.commentBoxWrapper} hidden={!score}>
            <p>
              What is the primary reason for your score? <span>(Optional)</span>
            </p>
            {/* @ts-expect-error: prop type error */}
            <TextareaAutosize
              rows={2}
              value={comment}
              onChange={(e) => handleCommentChange(e)}
            />
            <span>
              {comment?.length || 0}/{MAX_COMMENT_LENGTH}
            </span>
          </div>
        </div>
        <div hidden={!clickedCancel} className={styles['confirm-cancel']}>
          Are you really sure you want to exit without submitting your survey?
        </div>
        <div className={styles.buttons}>
          <Button type="outline" onClick={handleCancel}>
            {clickedCancel ? 'Confirm Skip' : 'Skip'}
          </Button>
          <Button disabled={!score} onClick={handleSubmit}>
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
}

export default NPSSurvey;
